import { useEffect, useMemo, useRef, useState } from 'react';
import { OwnerSelector, type OwnerOption as BaseOwnerOption } from './OwnerSelector';
import type { RepoNamespace } from '~/components/header/GithubCreateRepoFlow';
import { Button } from '~/components/ui/Button';
import { Dialog, DialogDescription, DialogTitle, type DialogProps } from '~/components/ui/LegacyDialog';
import { useGithubToken, type GetGithubToken } from '~/lib/client-github';
import { getOrganizations, getRepositoryId, type Organization } from '~/lib/github-api';
import type { RepoFullName } from '~/types/github';
import type { UserData } from '~/types/user';
import { classNames } from '~/utils/classNames';
import { debounce } from '~/utils/debounce';
import { useInitRef } from '~/utils/init-ref';
import { NOOP } from '~/utils/noops';

const NAME_CHECK_DEBOUNCE = 300;

interface Props {
  username: string;
  user?: UserData;
  onClose: DialogProps['onClose'];
  onChoose: (repo: RepoNamespace) => Promise<void>;
}

interface OwnerOption extends BaseOwnerOption {
  allowed?: boolean;
}

export function GithubCreateRepoDialog(props: Props & { open: boolean }) {
  return props.open && <GithubCreateRepoDialogInner {...props} />;
}

export function GithubCreateRepoDialogInner({ username, user, onClose, onChoose }: Props) {
  const [fullRepo, setFullRepo] = useState({ id: '', owner: username, name: '' });
  const [busy, setBusy] = useState(false);
  const [taken, setTaken] = useState(false);

  const checkRef = useRef<{ task: Promise<boolean | null>; repo: RepoFullName }>();

  const [orgs, setOrgs] = useState<Organization[]>([]);

  const { githubOrgs } = user?.featureFlags ?? {};
  const getGithubToken = useGithubToken();

  const debouncedRef = useInitRef(() =>
    debounce((repo: RepoFullName, signal: AbortSignal) => {
      checkRef.current = { task: checkNameIsTaken(getGithubToken, repo, signal), repo };

      checkRef.current?.task.then((taken) => {
        if (taken != null) {
          setTaken(taken);
        }
      });
    }, NAME_CHECK_DEBOUNCE),
  );

  const nameCheckAfterInput = debouncedRef.current;

  useEffect(() => {
    async function retrieveOrgs() {
      if (!user?.featureFlags.githubOrgs) {
        return;
      }

      const token = await getGithubToken();

      if (!token) {
        return;
      }

      try {
        const orgs = await getOrganizations(token);

        setOrgs(orgs);
      } catch {
        // ignore
      }
    }

    retrieveOrgs();
  }, []);

  useEffect(() => {
    if (!fullRepo.name.trim()) {
      return NOOP;
    }

    const controller = new AbortController();

    nameCheckAfterInput(fullRepo, controller.signal);

    return () => controller.abort();
  }, [fullRepo, username]);

  async function chooseRepo() {
    setBusy(true);

    const check =
      checkRef.current?.repo === fullRepo ? checkRef.current.task : checkNameIsTaken(getGithubToken, fullRepo);

    const taken = await check;

    if (taken) {
      setTaken(true);
      setBusy(false);

      return;
    }

    try {
      await onChoose(fullRepo);
    } finally {
      setBusy(false);
    }
  }

  function updateRepo(repo: RepoNamespace) {
    setFullRepo(repo);
    setTaken(false);

    checkRef.current = undefined;
  }

  const users = useMemo(() => {
    const options: OwnerOption[] = [
      { username, user, allowed: true as boolean | undefined, id: '__dummy__' },
      ...orgs.map((org) => ({
        id: org.id,
        allowed: org.viewerCanCreateRepositories,
        username: org.login,
        user: {
          name: org.login,
          avatar: org.avatarUrl,
        },
      })),
    ];

    if (!githubOrgs) {
      return options[0];
    }

    return options;
  }, [username, user, orgs, githubOrgs]);

  return (
    <GithubCreateRepoDialogControlled
      {...{ repo: fullRepo, busy, taken }}
      users={users}
      onChange={updateRepo}
      onClose={onClose}
      onChoose={chooseRepo}
    />
  );
}

const REPO_NAME_REGEX = /^[a-zA-Z0-9._-]+$/;

export function GithubCreateRepoDialogControlled({
  taken,
  busy,
  repo,
  onClose,
  onChoose,
  onChange,
  users,
}: {
  repo: RepoNamespace;
  busy: boolean;
  onChange: (repo: RepoNamespace) => void;
  users: Array<OwnerOption> | OwnerOption;
  onClose: DialogProps['onClose'];
  onChoose: () => void;
  taken: boolean;
}) {
  const actualRepoName = repo.name.trim();
  const invalid = Boolean(actualRepoName) && !REPO_NAME_REGEX.test(repo.name);

  const [selectedUser, setSelectedUser] = useState(Array.isArray(users) ? users[0] : users);

  const canCreate = selectedUser.allowed !== false;
  const error = taken || invalid || !canCreate;

  function handleUserChange(userOrOrg: OwnerOption) {
    setSelectedUser(userOrOrg);

    onChange({ owner: userOrOrg.username, name: '', id: userOrOrg.id, avatarUrl: userOrOrg.user?.avatar });
  }

  return (
    <Dialog onClose={onClose} open maxWidth={600}>
      <DialogTitle className="p-6 font-bold text-lg border-b-1px border-bolt-elements-borderColor no-outline">
        <span className="flex items-center gap-1 text-lg">
          <span className="i-bolt:logos-github?mask text-2xl"></span>
          <span className="font-semibold">Create a repository</span>
        </span>
      </DialogTitle>
      <DialogDescription>
        <div className="flex flex-col w-full items-start">
          <label
            htmlFor="github-choose-dialog-repo-name-input"
            className="text-bolt-elements-textSecondary mb-3 text-sm"
          >
            Choose a name for your repository
          </label>

          <div className="flex gap-5 w-full justify-between items-center mb-1">
            <OwnerSelector
              id="github-create-repo-dialog-user-select"
              owners={users}
              owner={selectedUser}
              onChange={handleUserChange}
            />
            /
            <input
              id="github-choose-dialog-repo-name-input"
              className="grow rounded outline-none bg-bolt-elements-background-depth-4 p-6px pl-2 light:placeholder:text-black/30 dark:placeholder:text-white/30"
              placeholder="your-repository"
              type="text"
              disabled={!canCreate}
              value={repo.name}
              onChange={(e) =>
                onChange({
                  owner: selectedUser.username,
                  name: e.target.value,
                  id: selectedUser.id,
                  avatarUrl: selectedUser.user?.avatar,
                })
              }
              onKeyDown={(e) => e.key === 'Enter' && onChoose()}
            />
          </div>

          <p
            className={classNames('text-right w-full text-sm mb-8', {
              'text-bolt-elements-item-contentDanger': error,
              'text-bolt-elements-textSecondary': !error,
            })}
          >
            {taken && `${repo.owner}/${repo.name} already exists, please choose a different repository`}

            {!taken && invalid && `Repository names can only contain letters, digits and the symbols ., _ and -.`}

            {!(taken || invalid) &&
              !canCreate &&
              'You do not have permission to create repositories in this organization'}

            {!error && 'This will create a new private repository in GitHub'}
          </p>

          <div className="flex w-full justify-between items-center">
            <div className="text-sm text-bolt-elements-textTertiary">
              New to GitHub?{' '}
              <a className="text-inherit!" href="https://support.bolt.new/integrations/git" target="_blank">
                Learn more
              </a>
            </div>
            <Button size="lg" disabled={!actualRepoName || error || busy} onClick={onChoose} variant="primary">
              <div className="flex items-center">
                {busy && <div className="i-svg-spinners:90-ring-with-bg size-4 mr-1" />}
                <div>{busy ? 'Creating repository' : 'Create repository'}</div>
              </div>
            </Button>
          </div>
        </div>
      </DialogDescription>
    </Dialog>
  );
}

// might return false negatives
async function checkNameIsTaken(getGithubToken: GetGithubToken, repo: RepoFullName, signal?: AbortSignal) {
  if (signal?.aborted) {
    return null;
  }

  const token = await getGithubToken();

  if (!token) {
    return false;
  }

  if (signal?.aborted) {
    return null;
  }

  try {
    await getRepositoryId(repo, token);
  } catch {
    return false;
  }

  if (signal?.aborted) {
    return null;
  }

  return true;
}
